import { useEffect, useMemo, useState } from 'react';
import { useStickyState } from '@/hooks/useStickyFilters';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { hasLanding, reconLanding } from '@/pages/reportLanding';
import { useQuery } from '@tanstack/react-query';
import { type ReconciliationRow } from '@/api/documents';
import { reconciliationActs } from '@/api/settlements';
import { contractors as contractorsApi, orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { AgGridReact } from 'ag-grid-react';
import { GridMoreRightButton, useAgMoreRight } from '@/shared/components/GridMoreRight';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import DatePopover from '@/components/DatePopover';
import SearchSelect from '@/components/SearchSelect';
import { pickDefaultOrg, useDefaultPeriod } from '@/store/defaults';
import { DOC_TABLE_NAMES_UK } from '@/utils/moduleNames';
import { api } from '@/api/client';
import { contractLabel } from '@/shared/utils/labels';
import { actPeriod } from '@/pages/reconciliationActPeriod';

const fmt = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

// Сальдо: на нашу користь — зелене, наш борг — червоне, нуль — звичайний
// текст. ч.1034: токени замість #15803d/#b91c1c/#000 — чорний нуль уночі
// зливався з фоном (Закон дизайну #11).
const balanceColor = (v: number) =>
  v > 0 ? 'var(--badge-green-fg)' : v < 0 ? 'var(--badge-red-fg)' : 'var(--text)';

const DOC_LABELS: Record<string, string> = {
  'doc.goods_receipts': 'Надходження',
  'doc.goods_sales': 'Реалізація',
  'doc.bank_receipts': 'Банк (надход.)',
  'doc.bank_payments': 'Банк (виплата)',
  'doc.cash_receipts': 'ПКО',
  'doc.cash_payments': 'РКО',
  'doc.goods_returns_from_customer': 'Повернення від покупця',
  'doc.goods_returns_to_supplier': 'Повернення постачальнику',
  'doc.debt_adjustments': 'Коригування боргу',
  'doc.additional_expenses': 'Дод. витрати',
};

export default function ReconciliationActPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });

  // Режим (рішення власника 2026-08-19): «Загалом» — построково ВСІ
  // контрагенти з Дт/Кт/сальдо за період; «По контрагенту» — як раніше.
  // ч.951 (баг власника): фільтри ЗБЕРІГАЮТЬСЯ — перехід на іншу сторінку і
  // назад більше їх не скидає (useStickyState → uiPrefs, профіль юзера).
  const [mode, setMode] = useStickyState<'total' | 'single'>('reconciliation-act.mode', 'total');
  const [orgId, setOrgId] = useStickyState('reconciliation-act.orgId', '');
  const [contractorId, setContractorId] = useStickyState('reconciliation-act.contractorId', '');
  const [contractId, setContractId] = useStickyState('reconciliation-act.contractId', '');
  // Період за замовчуванням — з ВЕРХНЬОГО фільтра шапки (рішення власника
  // 2026-08-19); «всі періоди» = порожні поля.
  const { dateFrom: gFrom, dateTo: gTo } = useDefaultPeriod();
  const [from, setFrom] = useStickyState<string>(
    'reconciliation-act.from', gFrom?.slice(0, 10) ?? '');
  const [to, setTo] = useStickyState<string>(
    'reconciliation-act.to', gTo?.slice(0, 10) ?? '');
  // «Загалом» НЕ вантажиться сам — лише кнопкою «сформувати».
  const [onlyDebts, setOnlyDebts] = useStickyState('reconciliation-act.onlyDebts', false);
  const [applied, setApplied] = useState<{
    from: string; to: string; onlyDebts: boolean } | null>(null);

  // ч.1035: «усі →» з віджета боргів головної — «Загалом», список одразу
  // (без «сформувати»), «лише борги». Параметри одноразові: читаємо і
  // прибираємо з адреси, щоб F5 не перезаписував вибір користувача.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!hasLanding(searchParams)) return;
    const l = reconLanding(searchParams);
    if (l.organization_id) setOrgId(l.organization_id);
    if (l.form) {
      setMode('total');
      if (l.onlyDebts) setOnlyDebts(true);
      setApplied({ from, to, onlyDebts: l.onlyDebts || onlyDebts });
    }
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!orgId && orgList.data?.items[0]) setOrgId(pickDefaultOrg(orgList.data.items)!.id);

  const contractsQ = useQuery({
    queryKey: ['contracts', orgId, contractorId],
    queryFn: () => refs.contracts({ organization_id: orgId, contractor_id: contractorId }),
    enabled: mode === 'single' && !!orgId && !!contractorId,
  });

  const act = useQuery({
    queryKey: ['reconciliation-act', orgId, contractorId, contractId, from, to],
    // ЄДИНЕ джерело з документом-актом і «Загалом» — БУХ-ПРОВОДКИ по
    // рахунках звірки (contractor-detail). Старий звіт читав РЕЄСТР
    // розрахунків → на мігрованих базах цифри монітора і акта
    // розходились (факт Кухарев 2026-08-19: −5 277 500,94 проти акта).
    queryFn: async () => {
      const r = await api.get<{ opening: string; closing: string;
        rows: { date: string; doc_type: string; doc_id: string;
          doc_number: string | null; debit: string | null;
          credit: string | null }[] }>(
        '/reports/reconciliation-act/contractor-detail', { params: {
          organization_id: orgId, contractor_id: contractorId,
          contract_id: contractId || undefined,
          date_from: from || '1990-01-01',
          date_to: to || '2099-12-31',
        } });
      // Мапа у старий формат сторінки (running balance по рядках).
      let bal = Number(r.data.opening);
      let td = 0; let tc = 0;
      const rows = r.data.rows.map((x) => {
        const d = x.debit ? Number(x.debit) : 0;
        const c = x.credit ? Number(x.credit) : 0;
        td += d; tc += c; bal += d - c;
        return {
          document_id: x.doc_id, movement_date: x.date,
          document_type: x.doc_type, document_number: x.doc_number ?? '',
          debit: String(d), credit: String(c), balance: String(bal),
          direction: (d > 0 ? '+' : '-') as '+' | '-',
        };
      });
      return {
        opening_balance: r.data.opening, closing_balance: r.data.closing,
        turnover_debit: String(td), turnover_credit: String(tc), rows,
      };
    },
    enabled: mode === 'single' && !!orgId && !!contractorId,
  });
  // ч.1034 (баг власника): react-query ТРИМАЄ дані вимкненого запиту, тож
  // після переходу на «Загалом» плашка сальдо обраного контрагента і кнопка
  // «зафіксувати акт звірки» лишались на екрані. Усе, що належить вкладці
  // «По контрагенту», малюємо лише з `singleAct` — поза нею його немає.
  const singleAct = mode === 'single' ? act.data : undefined;

  // Попап-довідка про документ рядка (рішення власника 2026-08-19):
  // платіжки — дата/сума/призначення; товарні — номенклатура/к-ть/ціна/сума.
  const [docInfo, setDocInfo] = useState<{
    row: ReconciliationRow; loading: boolean;
    data?: { number: string | null; date: string | null; amount: string | null;
      extra: string | null;
      items: { name: string; quantity: string; price: string; total: string }[] };
    error?: string;
  } | null>(null);
  const openDocInfo = (r: ReconciliationRow) => {
    setDocInfo({ row: r, loading: true });
    api.get('/reports/reconciliation-act/doc-info', { params: {
      organization_id: orgId, document_type: r.document_type,
      document_id: r.document_id,
    } }).then((resp) => setDocInfo({ row: r, loading: false, data: resp.data }))
      .catch((e) => setDocInfo({ row: r, loading: false,
        error: e?.response?.data?.detail ?? 'Довідка недоступна' }));
  };

  const cols = useMemo<ColumnDef<ReconciliationRow>[]>(() => [
    {
      key: 'movement_date', header: 'Дата', width: 160,
      sortValue: (r) => r.movement_date ?? '',
      cell: (r) => r.movement_date ? new Date(r.movement_date).toLocaleString('uk-UA') : '',
    },
    {
      key: 'document_type', header: 'Документ', width: 200,
      sortValue: (r) => DOC_LABELS[r.document_type] ?? DOC_TABLE_NAMES_UK[r.document_type] ?? r.document_type,
      cell: (r) => (
        <a style={{ cursor: 'pointer' }}
          title="Показати деталі документа"
          onClick={(e) => { e.stopPropagation(); openDocInfo(r); }}>
          {DOC_LABELS[r.document_type] ?? DOC_TABLE_NAMES_UK[r.document_type] ?? r.document_type}
        </a>
      ),
    },
    {
      key: 'document_number', header: '№', width: 130,
      sortValue: (r) => r.document_number ?? '',
      cell: (r) => r.document_number,
    },
    {
      key: 'debit', header: 'Дебет (на нашу користь)', width: 180, align: 'right',
      sortValue: (r) => Number(r.debit),
      cell: (r) => r.debit && Number(r.debit) > 0 ? fmt(r.debit) : '',
    },
    {
      key: 'credit', header: 'Кредит (наш борг ↓ або вони ↓)', width: 220, align: 'right',
      sortValue: (r) => Number(r.credit),
      cell: (r) => r.credit && Number(r.credit) > 0 ? fmt(r.credit) : '',
    },
    {
      key: 'balance', header: 'Сальдо', width: 140, align: 'right',
      sortValue: (r) => Number(r.balance),
      cell: (r) => {
        const v = Number(r.balance);
        return <span style={{ fontWeight: 600, color: balanceColor(v) }}>{fmt(r.balance)}</span>;
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [orgId]);

  // Фіксація документа-акта з цифр звіту (2026-08-12, рішення власника).
  // ctrOverride — фіксація з попапа рядка «Загалом» (контрагент рядка).
  const fixAct = useMutation({
    mutationFn: (ctrOverride?: string) => reconciliationActs.fromRegisters({
      organization_id: orgId,
      contractor_id: ctrOverride || contractorId,
      contract_id: ctrOverride ? null : (contractId || null),
      // Порожні поля («усі дати») ламали 422, який маскувався текстом про
      // права (факт 2026-08-19): порожньо = від 1990 до сьогодні.
      // ч.1034: період — той, за який ПОКАЗАНО цифри (див. actPeriod).
      ...actPeriod(!!ctrOverride, applied, { from, to },
        new Date().toISOString().slice(0, 10)),
      document_date: new Date().toISOString(),
      comment: null,
    }),
    onSuccess: (doc) => navigate(`/settlements/reconciliation-acts/${doc.id}`),
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } })
        ?.response?.data?.detail;
      window.alert('Не вдалося зафіксувати акт: '
        + (typeof detail === 'string' ? detail
          : detail ? JSON.stringify(detail).slice(0, 300)
            : 'перевірте права (documents.reconciliation_acts.create) або повторіть вхід.'));
    },
  });

  // «Загалом»: серверна пагінація (Норма #29 — контрагентів можуть бути
  // тисячі; агрегат по всій базі ГАЛІС за 10 років — 64 мс, перевірено).
  interface SummaryRow {
    contractor_id: string; name: string; code: string | null;
    opening: string; debit: string; credit: string; closing: string;
  }
  const moreSum = useAgMoreRight<SummaryRow>(); // ч.1316: «ще колонки →»
  const { datasource: sumDs, total: sumTotal } =
    useInfiniteGridDatasource<SummaryRow>(
      // 3-й аргумент — СЕРВЕРНЕ сортування (Infinite Row Model не сортує
      // локально; клік по заголовку → sortModel → sort_by/sort_dir).
      (limit, offset, sort) => {
        // Дані НЕ вантажаться самі (рішення власника 2026-08-19) — лише
        // після «сформувати» (applied). Порожній orgId теж не смикаємо
        // (422 при першому рендері).
        if (!orgId || !applied) return Promise.resolve({ items: [], total: 0 });
        return api.get('/reports/reconciliation-act/summary', {
          params: { organization_id: orgId,
            date_from: applied.from || '1990-01-01',
            date_to: applied.to || '2099-12-31',
            only_debts: applied.onlyDebts || undefined,
            sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset },
        }).then((r) => r.data);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [orgId, applied, mode === 'total'],
    );
  const [detail, setDetail] = useState<{
    row: SummaryRow; loading: boolean;
    data?: { opening: string; closing: string;
      rows: { date: string; doc_type: string; doc_number: string | null;
        debit: string | null; credit: string | null }[] };
  } | null>(null);
  const openDetail = (row: SummaryRow) => {
    setDetail({ row, loading: true });
    api.get('/reports/reconciliation-act/contractor-detail', { params: {
      organization_id: orgId, contractor_id: row.contractor_id,
      // Той самий період, за який СФОРМОВАНО таблицю; порожньо = весь час
      // (сирі порожні дати давали 422 — факт 2026-08-19). ч.1034: порожній
      // сформований період («весь час») більше не підміняється поточними
      // полями «З/По», змінені після «сформувати».
      date_from: (applied ?? { from }).from || '1990-01-01',
      date_to: (applied ?? { to }).to || '2099-12-31',
    } }).then((r) => setDetail({ row, loading: false, data: r.data }))
      .catch(() => setDetail(null));
  };

  const balance = Number(singleAct?.closing_balance ?? 0);
  const balanceLabel = balance > 0
    ? `Контрагент винен нам ${fmt(balance)} грн`
    : balance < 0
    ? `Ми винні контрагенту ${fmt(-balance)} грн`
    : 'Розрахунки збалансовано (0 грн)';

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Монітор розрахунків</h2>
        {singleAct && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {/* «Створити коригування» перенесено у РЕДАКТОР АКТА
                (рішення власника 2026-08-19): коригування логічно робити
                за підсумком звірки; напряму — /debt-adjustments/new. */}
            <button type="button" className="primary"
              onClick={() => fixAct.mutate(undefined)}
              disabled={fixAct.isPending}
              title="Створити документ «Акт звірки» з цифрами звіту (чернетка): далі відправка контрагенту і підтвердження">
              {fixAct.isPending ? 'фіксуємо…' : 'зафіксувати акт звірки'}
            </button>
          </div>
        )}
      </div>

      <div className="seg-tabs">
        <button type="button" className={mode === 'total' ? 'active' : ''}
          onClick={() => setMode('total')}>Загалом</button>
        <button type="button" className={mode === 'single' ? 'active' : ''}
          onClick={() => setMode('single')}>По контрагенту</button>
      </div>

      <div className="doc-grid doc-grid--5" style={{ marginBottom: '1rem' }}>
        <div className="field">
          <label>Організація</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            <option value="">—</option>
            {orgList.data?.items.map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
        </div>
        {mode === 'single' ? (<>
        <div className="field">
          <label>Контрагент</label>
          <SearchSelect
            items={(contractorsQ.data?.items ?? []).map((c) => ({
              id: c.id, label: c.name, hint: c.edrpou ?? null,
            }))}
            value={contractorId}
            onChange={(id) => { setContractorId(id); setContractId(''); }}
          />
        </div>
        <div className="field">
          <label>Договір (всі)</label>
          <select value={contractId} onChange={(e) => setContractId(e.target.value)} disabled={!contractorId}>
            <option value="">— всі договори —</option>
            {contractsQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{contractLabel(c)}</option>
            ))}
          </select>
        </div>
        </>) : null}
        <div className="field">
          <label>З</label>
          <DatePopover value={from} onChange={(v) => setFrom(v)} />
        </div>
        <div className="field">
          <label>По</label>
          <DatePopover value={to} onChange={(v) => setTo(v)} />
        </div>
        {mode === 'total' && (
          <div className="field" style={{ display: 'flex', alignItems: 'flex-end',
            gap: '0.6rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem',
              cursor: 'pointer', whiteSpace: 'nowrap', marginBottom: 6 }}>
              <input type="checkbox" checked={onlyDebts}
                style={{ width: 'auto' }}
                onChange={(e) => setOnlyDebts(e.target.checked)} />
              лише борги
            </label>
            <button className="primary" disabled={!orgId}
              onClick={() => setApplied({ from, to, onlyDebts })}>
              сформувати
            </button>
          </div>
        )}
      </div>

      {mode === 'single' && act.isError && (
        <div style={{
          padding: '0.75rem', borderRadius: 6, marginBottom: '1rem',
          background: 'var(--badge-red-bg)', border: '1px solid var(--badge-red-bd)', color: 'var(--badge-red-fg)',
        }}>
          Не вдалося завантажити звіт. Оновіть сторінку; якщо не допомагає —
          повторіть вхід у систему.
        </div>
      )}
      {singleAct && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '0.5rem', padding: '0.75rem', background: 'var(--bg-soft-2)',
          borderRadius: 6, marginBottom: '1rem',
        }}>
          <div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Сальдо на початок</div>
            <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>{fmt(singleAct.opening_balance)}</div>
          </div>
          <div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Оборот по дебету</div>
            <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>{fmt(singleAct.turnover_debit)}</div>
          </div>
          <div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Оборот по кредиту</div>
            <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>{fmt(singleAct.turnover_credit)}</div>
          </div>
          <div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Сальдо на кінець</div>
            <div style={{ fontWeight: 700, fontSize: '1.1rem', color: balanceColor(balance) }}>
              {balanceLabel}
            </div>
          </div>
        </div>
      )}

      {docInfo && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)',
          zIndex: 1100, display: 'flex', alignItems: 'center',
          justifyContent: 'center' }}
          onClick={() => setDocInfo(null)}>
          <div style={{ background: 'var(--bg-panel)', borderRadius: 8,
            width: 'min(640px, 92vw)', maxHeight: '80vh', overflowY: 'auto',
            boxShadow: '0 20px 50px rgba(0,0,0,0.35)', padding: '1rem' }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.5rem' }}>
              {DOC_LABELS[docInfo.row.document_type] ?? DOC_TABLE_NAMES_UK[docInfo.row.document_type] ?? docInfo.row.document_type}
              {docInfo.data?.number ? ` № ${docInfo.data.number}` : ''}
            </h3>
            {docInfo.loading ? (
              <p style={{ color: 'var(--muted)' }}>Завантаження…</p>
            ) : docInfo.error ? (
              <p style={{ color: 'var(--badge-red-fg)' }}>{docInfo.error}</p>
            ) : docInfo.data && (
              <>
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap',
                  fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                  {docInfo.data.date && (
                    <span><b>Дата:</b>{' '}
                      {new Date(docInfo.data.date).toLocaleDateString('uk-UA')}</span>
                  )}
                  {docInfo.data.amount && (
                    <span><b>Сума:</b> {fmt(docInfo.data.amount)} грн</span>
                  )}
                </div>
                {docInfo.data.extra && (
                  <p style={{ fontSize: '0.88rem', margin: '0 0 0.5rem' }}>
                    <b>Призначення / підстава:</b> {docInfo.data.extra}
                  </p>
                )}
                {docInfo.data.items.length > 0 && (
                  <table style={{ width: '100%', borderCollapse: 'collapse',
                    fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
                        <th style={{ padding: '0.3rem' }}>Номенклатура</th>
                        <th style={{ padding: '0.3rem', width: 90, textAlign: 'right' }}>К-ть</th>
                        <th style={{ padding: '0.3rem', width: 110, textAlign: 'right' }}>Ціна</th>
                        <th style={{ padding: '0.3rem', width: 120, textAlign: 'right' }}>Сума</th>
                      </tr>
                    </thead>
                    <tbody>
                      {docInfo.data.items.map((it, i) => (
                        <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                          <td style={{ padding: '0.3rem' }}>{it.name}</td>
                          <td style={{ padding: '0.3rem', textAlign: 'right' }}>
                            {Number(it.quantity).toLocaleString('uk-UA')}
                          </td>
                          <td style={{ padding: '0.3rem', textAlign: 'right' }}>{fmt(it.price)}</td>
                          <td style={{ padding: '0.3rem', textAlign: 'right' }}>{fmt(it.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
            <div style={{ textAlign: 'right', marginTop: '0.75rem' }}>
              <button onClick={() => setDocInfo(null)}>закрити</button>
            </div>
          </div>
        </div>
      )}

      {mode === 'single' && (
      <DataTable<ReconciliationRow>
        rows={singleAct?.rows ?? []}
        columns={cols}
        rowKey={(r) => `${r.document_id}-${r.movement_date}-${r.direction}`}
        loading={act.isLoading}
        emptyText={contractorId
          ? 'За обраний період рухів з контрагентом немає. Якщо очікували побачити операції — розширте період («З»): останні операції могли бути раніше.'
          : 'Оберіть контрагента, щоб побудувати акт звірки.'}
      />
      )}

      {mode === 'total' && (
        <>
          <p style={{ color: 'var(--muted)', fontSize: '0.82rem', margin: '0 0 0.4rem' }}>
            {applied
              ? <>Контрагентів: {sumTotal ?? '…'}. Клік по рядку — операції
                  контрагента і фіксація акта звірки.</>
              : 'Оберіть період (порожньо = за весь час) і натисніть «сформувати».'}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}><GridMoreRightButton more={moreSum} /></div>
          <div className="ag-theme-quartz grid" style={{ height: 'calc(100vh - 320px)' }}>
            <AgGridReact<SummaryRow>
              rowModelType="infinite"
              datasource={sumDs}
              cacheBlockSize={50}
              {...moreSum.gridProps}
              onRowClicked={(e) => e.data && openDetail(e.data)}
              defaultColDef={{
                sortable: true,
                headerClass: 'ag-center-header',
                cellStyle: { textAlign: 'center' },
              }}
              columnDefs={[
                { field: 'name', headerName: 'Контрагент', flex: 2,
                  cellStyle: { cursor: 'pointer', textAlign: 'center' } },
                { field: 'code', headerName: 'Код', width: 120 },
                { field: 'opening', headerName: 'Сальдо поч.', width: 140,
                  valueFormatter: (p) => p.value != null ? fmt(p.value) : '' },
                { field: 'debit', headerName: 'Дебет', width: 150,
                  valueFormatter: (p) => p.value != null ? fmt(p.value) : '' },
                { field: 'credit', headerName: 'Кредит', width: 150,
                  valueFormatter: (p) => p.value != null ? fmt(p.value) : '' },
                { field: 'closing', headerName: 'Сальдо кін.', width: 150,
                  cellStyle: (p) => ({
                    fontWeight: 600, textAlign: 'center',
                    color: balanceColor(Number(p.value)),
                  }),
                  valueFormatter: (p) => p.value != null ? fmt(p.value) : '' },
              ]}
            />
          </div>
        </>
      )}

      {detail && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)',
          zIndex: 1100, display: 'flex', alignItems: 'center',
          justifyContent: 'center' }} onClick={() => setDetail(null)}>
          <div style={{ background: 'var(--bg-panel)', borderRadius: 8,
            width: 'min(760px, 94vw)', maxHeight: '84vh', display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 50px rgba(0,0,0,0.35)', padding: '1rem' }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.5rem' }}>{detail.row.name}</h3>
            {detail.loading ? (
              <p style={{ color: 'var(--muted)' }}>Завантаження…</p>
            ) : detail.data && (
              <div style={{ overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse',
                  fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
                      <th style={{ padding: '0.3rem', width: 100 }}>Дата</th>
                      <th style={{ padding: '0.3rem' }}>Документ</th>
                      <th style={{ padding: '0.3rem', width: 120, textAlign: 'right' }}>Дебет</th>
                      <th style={{ padding: '0.3rem', width: 120, textAlign: 'right' }}>Кредит</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ fontWeight: 600, borderTop: '1px solid var(--border)' }}>
                      <td colSpan={3} style={{ padding: '0.3rem' }}>Сальдо початкове</td>
                      <td style={{ padding: '0.3rem', textAlign: 'right' }}>
                        {fmt(detail.data.opening)}
                      </td>
                    </tr>
                    {detail.data.rows.map((r, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '0.3rem' }}>
                          {new Date(r.date).toLocaleDateString('uk-UA')}
                        </td>
                        <td style={{ padding: '0.3rem' }}>
                          {DOC_LABELS[r.doc_type] ?? DOC_TABLE_NAMES_UK[r.doc_type] ?? r.doc_type}
                          {r.doc_number ? ` № ${r.doc_number}` : ''}
                        </td>
                        <td style={{ padding: '0.3rem', textAlign: 'right' }}>
                          {r.debit ? fmt(r.debit) : ''}
                        </td>
                        <td style={{ padding: '0.3rem', textAlign: 'right' }}>
                          {r.credit ? fmt(r.credit) : ''}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ fontWeight: 700, borderTop: '1px solid var(--border-strong)' }}>
                      <td colSpan={3} style={{ padding: '0.3rem' }}>Сальдо кінцеве</td>
                      <td style={{ padding: '0.3rem', textAlign: 'right' }}>
                        {fmt(detail.data.closing)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem',
              marginTop: '0.75rem' }}>
              <button onClick={() => setDetail(null)}>закрити</button>
              <button className="primary" disabled={fixAct.isPending}
                title="Створити документ «Акт звірки» з цими цифрами (чернетка)"
                onClick={() => fixAct.mutate(detail.row.contractor_id)}>
                {fixAct.isPending ? '…' : 'зафіксувати акт звірки'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
